import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Lock, Mail, Eye, EyeOff, Shield, UserPlus, KeyRound, Clock, ShieldAlert } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Login({ onLogin }) {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isFirstUser, setIsFirstUser] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  // Read once on mount so the banner persists until the user logs back in
  // OR refreshes the login page. Cleared on first successful login.
  const [logoutReason] = useState(() => {
    try {
      const r = window.localStorage.getItem('logout_reason');
      return r || null;
    } catch {
      return null;
    }
  });
  
  const { login } = useAuth();

  useEffect(() => {
    // Check if any users exist
    const checkAuthStatus = async () => {
      try {
        const response = await axios.get(`${API}/auth/check`);
        setIsFirstUser(!response.data.users_exist);
      } catch (error) {
        console.error('Auth check failed:', error);
      } finally {
        setCheckingStatus(false);
      }
    };
    checkAuthStatus();
  }, []);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (newPassword.length < 8) {
      toast.error('New password must be at least 8 characters');
      return;
    }
    
    if (newPassword !== confirmNewPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: password,
        new_password: newPassword
      });
      toast.success('Password changed successfully!');
      setMustChangePassword(false);
      navigate('/dashboard', { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isFirstUser) {
        // Register first user
        if (password !== confirmPassword) {
          toast.error('Passwords do not match');
          setLoading(false);
          return;
        }
        
        await axios.post(`${API}/auth/register-first`, { email, password });
        toast.success('Account created! Logging you in...');
      }
      
      // Login
      const result = await login(email, password);
      if (result.success) {
        if (result.mustChangePassword) {
          // Show password change form
          setMustChangePassword(true);
          toast.info('Please change your password to continue');
        } else {
          // Let the route swap to SmartRedirect (the /login route becomes
          // SmartRedirect once authenticated) own the redirect. It consumes
          // localStorage.post_login_redirect and returns the user to the record
          // they deep-linked to (or the dashboard otherwise). Navigating here too
          // would race with that and land everyone on the dashboard.
          try { localStorage.removeItem('logout_reason'); } catch (e) { /* ignore */ }
          toast.success('Login successful');
        }
      } else {
        toast.error(result.message || 'Login failed');
      }
    } catch (error) {
      const message = error.response?.data?.detail || 
                      error.response?.data?.message || 
                      'An error occurred. Please try again.';
      
      // Handle password validation errors
      if (Array.isArray(error.response?.data?.detail)) {
        const errors = error.response.data.detail.map(e => e.msg).join('. ');
        toast.error(errors);
      } else {
        toast.error(message);
      }
    } finally {
      setLoading(false);
    }
  };

  if (checkingStatus) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      <div
        className="hidden lg:flex lg:w-1/2 relative bg-cover bg-center"
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1621953703922-8146d7127225?crop=entropy&cs=srgb&fm=jpg&q=85')",
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-[#1a365d]/95 to-[#1a365d]/80"></div>
        <div className="relative z-10 flex flex-col justify-center p-12 text-white">
          <div className="bg-white rounded-lg p-4 mb-6 w-fit">
            <img 
              src="/logo.png" 
              alt="Elite Home Rehab" 
              className="h-20 object-contain"
            />
          </div>
          <p className="text-xl text-white/90 leading-relaxed mb-8">
            Streamline your patient visit tracking and reporting with intelligent
            data management.
          </p>
          <div className="flex items-center gap-3 text-white/80">
            <Shield size={24} />
            <span>HIPAA Compliant • Secure • Encrypted</span>
          </div>
        </div>
      </div>

      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Password Change Form */}
          {mustChangePassword ? (
            <>
              <div className="mb-8">
                <h2 className="font-heading text-3xl font-semibold text-foreground mb-2">
                  Change Your Password
                </h2>
                <p className="text-muted-foreground">
                  Please create a new password to continue
                </p>
              </div>

              <form onSubmit={handleChangePassword} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="newPassword">New Password</Label>
                  <div className="relative">
                    <KeyRound
                      className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground"
                      size={18}
                    />
                    <Input
                      id="newPassword"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter new password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="pl-10 pr-10"
                      required
                      minLength={8}
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Must be at least 8 characters
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmNewPassword">Confirm New Password</Label>
                  <div className="relative">
                    <Lock
                      className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground"
                      size={18}
                    />
                    <Input
                      id="confirmNewPassword"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Confirm new password"
                      value={confirmNewPassword}
                      onChange={(e) => setConfirmNewPassword(e.target.value)}
                      className="pl-10"
                      required
                      autoComplete="new-password"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  disabled={loading}
                  data-testid="change-password-btn"
                >
                  {loading ? 'Changing Password...' : 'Change Password & Continue'}
                </Button>
              </form>
            </>
          ) : (
            <>
              <div className="mb-8">
                <h2 className="font-heading text-3xl font-semibold text-foreground mb-2">
                  {isFirstUser ? 'Create Admin Account' : 'Welcome Back'}
                </h2>
                <p className="text-muted-foreground">
                  {isFirstUser 
                    ? 'Set up your secure admin account to get started'
                    : 'Enter your credentials to access the dashboard'
                  }
                </p>
              </div>

              {logoutReason && !isFirstUser && (
                <div
                  className={`mb-5 px-4 py-3 rounded-lg border flex items-start gap-3 ${
                    logoutReason === 'inactivity'
                      ? 'bg-amber-50 border-amber-200 text-amber-900'
                      : 'bg-red-50 border-red-200 text-red-900'
                  }`}
                  data-testid={`logout-reason-${logoutReason}`}
                >
                  {logoutReason === 'inactivity' ? (
                    <Clock size={18} className="shrink-0 mt-0.5" />
                  ) : (
                    <ShieldAlert size={18} className="shrink-0 mt-0.5" />
                  )}
                  <div className="text-sm">
                    <p className="font-semibold">
                      {logoutReason === 'inactivity'
                        ? 'You were signed out for inactivity'
                        : 'Your session expired'}
                    </p>
                    <p className="text-xs mt-0.5 opacity-90">
                      {logoutReason === 'inactivity'
                        ? "Sign back in to pick up where you left off — we'll bring you straight back."
                        : 'For your security, please sign in again to continue.'}
                    </p>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <div className="relative">
                    <Mail
                      className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground"
                      size={18}
                    />
                    <Input
                      id="email"
                      type="email"
                      placeholder="Enter your email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10"
                      data-testid="email-input"
                      required
                      autoComplete="email"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <div className="relative">
                    <Lock
                      className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground"
                      size={18}
                    />
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder={isFirstUser ? 'Create a strong password' : 'Enter your password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10 pr-10"
                      data-testid="password-input"
                      required
                      autoComplete={isFirstUser ? 'new-password' : 'current-password'}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  {isFirstUser && (
                <p className="text-xs text-muted-foreground">
                  Must be 8+ characters with uppercase, lowercase, number, and special character
                </p>
              )}
            </div>

            {isFirstUser && (
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <div className="relative">
                  <Lock
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground"
                    size={18}
                  />
                  <Input
                    id="confirmPassword"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Confirm your password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pl-10"
                    data-testid="confirm-password-input"
                    required
                    autoComplete="new-password"
                  />
                </div>
              </div>
            )}

            <Button
              type="submit"
              className="w-full h-11"
              disabled={loading}
              data-testid="login-button"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  {isFirstUser ? 'Creating Account...' : 'Logging in...'}
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  {isFirstUser ? <UserPlus size={18} /> : <Lock size={18} />}
                  {isFirstUser ? 'Create Account & Login' : 'Login'}
                </span>
              )}
            </Button>
          </form>

          <div className="mt-8 p-4 bg-muted rounded-lg">
            <div className="flex items-start gap-3">
              <Shield className="text-primary mt-0.5" size={20} />
              <div>
                <p className="text-sm font-medium text-foreground mb-1">HIPAA Compliant Security</p>
                <ul className="text-xs text-muted-foreground space-y-1">
                  <li>• 15-minute session timeout</li>
                  <li>• Encrypted password storage</li>
                  <li>• Complete audit logging</li>
                  <li>• Account lockout protection</li>
                </ul>
              </div>
            </div>
          </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
